import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { flagSystemCapabilityApi } from '../api/flagSystemApi';
import { flagError, positiveId } from '../utils/flagSystem';
import { FLAG_SYSTEM_DISABLED_EVENT } from '../utils/flagSystemCapability';

const useFlagSystemCapability = (customerId, refreshKey = '') => {
  const { accessContext, isAuthReady, isLoggedIn } = useAuth();
  const token = localStorage.getItem('token');
  const scope = useMemo(() => ({ customerId: Number(customerId), accessContext, token, refreshKey, isAuthReady, isLoggedIn }),
    [customerId, accessContext, token, refreshKey, isAuthReady, isLoggedIn]);
  const currentScope = useRef(scope);
  currentScope.current = scope;
  const sequence = useRef(0);
  const canLoad = Boolean(isAuthReady && isLoggedIn && token && accessContext && positiveId(customerId));
  const [state, setState] = useState({ scope: null, status: 'idle', data: null, error: '', refreshing: false });

  const load = useCallback(async ({ background = false, revoked = false } = {}) => {
    if (!canLoad) return;
    const request = ++sequence.current;
    setState(previous => {
      const reuse = background && previous.scope === scope && ['enabled', 'disabled', 'revoked'].includes(previous.status);
      return {
        scope, status: revoked ? 'revoked' : reuse ? previous.status : 'loading',
        data: reuse && !revoked ? previous.data : null, error: '', refreshing: true,
      };
    });
    try {
      const data = await flagSystemCapabilityApi.get(scope.customerId);
      if (currentScope.current !== scope || sequence.current !== request) return;
      setState({ scope, data, status: data.enabled ? (revoked ? 'revoked' : 'enabled') : 'disabled', error: '', refreshing: false });
      return data;
    } catch (error) {
      if (currentScope.current !== scope || sequence.current !== request) return;
      setState({
        scope, status: 'error', data: null, refreshing: false,
        error: error.response ? flagError(error, 'Unable to check Flag System access.') :
          'Unable to check Flag System access. Please try again.',
      });
    }
  }, [canLoad, scope]);

  useEffect(() => {
    load();
    return () => { sequence.current += 1; };
  }, [load]);

  useEffect(() => {
    const focus = () => load({ background: true });
    const disabled = event => {
      if (event.detail && event.detail.customerId === scope.customerId && event.detail.token === scope.token) {
        load({ revoked: true });
      }
    };
    window.addEventListener('focus', focus);
    window.addEventListener(FLAG_SYSTEM_DISABLED_EVENT, disabled);
    return () => {
      window.removeEventListener('focus', focus);
      window.removeEventListener(FLAG_SYSTEM_DISABLED_EVENT, disabled);
    };
  }, [load, scope]);

  const current = canLoad && state.scope === scope ? state :
    { status: canLoad ? 'loading' : 'idle', data: null, error: '', refreshing: false };
  const refetch = useCallback(() => load(), [load]);
  return { ...current, enabled: current.status === 'enabled', canRetry: canLoad, refetch };
};

export default useFlagSystemCapability;
